import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { twc } from 'react-twc';
import { useAccount, useWalletClient } from 'wagmi';

import { EthImg, NearImg, SolanaImg } from '@/assets';
import { HeroContainer, HeroSubValue, HeroTitle, HeroValue } from '@/components/hero';
import AllStakeTips from '@/components/tips';
import YourRestakedAmount from '@/components/your-restaked-amount';
import config from '@/config';
import { SymbolInput } from '@/config/type';
import { useWalletSelector } from '@/context/WalletSelectorContext';
import useChain from '@/hooks/useChain';
import usePoints from '@/hooks/usePoints';
import useQuery from '@/hooks/useQuery';
import { extractToTokenNameByName, isChainNativeToken } from '@/utils/regex';
import toLocaleString from '@/utils/toLocaleString';
import { resetWagmiState } from '@/utils/wagmi';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useWeb3Modal } from '@web3modal/wagmi/react';

function Portfolio() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const query = useQuery();
  const name = query.get('name');
  const symbol = query.get('symbol');

  const chain = useChain();
  const points = usePoints();

  const networkText = useMemo(() => {
    if (!chain) return '';
    switch (chain.chainId) {
      case 'btc':
        return t('chain.bitcoinMainnet');
      case 'eth':
        return t('chain.ethereumMainnet');
      case 'near':
        return t('chain.nearProtocol');
      case 'solana':
        return t('chain.solanaNetwork');
    }
  }, [chain, t]);

  useEffect(() => {
    if (!name) return;
    if (!isChainNativeToken(name)) return;
    if (!config.restakingTokens.find((token) => token.name === name)) {
      navigate('/portfolio');
    }
  }, [name, navigate]);

  useEffect(() => {
    if (!symbol) return;
    if (!isChainNativeToken(symbol)) return;
    navigate(`/portfolio?symbol=${extractToTokenNameByName(symbol)}`);
  }, [symbol, navigate]);

  if (!chain) return null;

  return (
    <HeroContainer>
      <HeroTitle>{t('portfolio.title', { network: networkText })}</HeroTitle>
      <HeroValue>
        <p>
          {points.points} {t('portfolio.points')}
        </p>
        {Object.keys(points.tokens).length > 0 && (
          <AllStakeTips>
            <div className="flex flex-col gap-2">
              {Object.keys(points.tokens)
                .map((addr) => ({
                  addr: addr,
                  points: points.tokens[addr],
                }))
                .filter(
                  (token) => config.restakingTokens.find((x) => x.address === token.addr)?.symbol,
                )
                .map((token, idx) => (
                  <p key={idx}>
                    {t('portfolio.pointsTip', {
                      amount: toLocaleString(token.points, 2),
                      symbol: config.restakingTokens.find((x) => x.address === token.addr)?.symbol,
                    })}
                  </p>
                ))}
            </div>
          </AllStakeTips>
        )}
      </HeroValue>
      <HeroSubValue>{t('portfolio.subTitle')}</HeroSubValue>
      {!chain.isSignedIn && <LoginModal />}
      {chain.isSignedIn && <YourRestakedAmount />}
    </HeroContainer>
  );
}

const LoginModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const solanaWalletModal = useWalletModal();
  const { modal: nearWalletModal } = useWalletSelector();
  const { open: ethModalOpen } = useWeb3Modal();
  const { data: walletClient } = useWalletClient();
  const ethAccount = useAccount();
  const chain = useChain();

  const onClickLogin = (chainId: string, callback: () => void) => {
    const token =
      config.restakingTokens.find((token: SymbolInput) => token.chain === chainId) ??
      config.restakingTokens[0];
    navigate(`${location.pathname}?symbol=${token.name}`);
    callback();
  };

  if (!chain) return null;

  return (
    <div className="my-[20vh] w-full md:w-[700px] px-5">
      <div className="flex flex-col gap-6">
        {!chain.isSignedIn && chain.chainId === 'near' && (
          <LoginButton onClick={() => onClickLogin('near', () => nearWalletModal.show())}>
            <img src={NearImg} width={24} height={24} />
            <p>
              {t('header.connectChainWallet', {
                chain: 'Near',
              })}
            </p>
          </LoginButton>
        )}
        {!chain.isSignedIn && chain.chainId === 'solana' && (
          <LoginButton
            onClick={() => onClickLogin('solana', () => solanaWalletModal.setVisible(true))}
          >
            <img src={SolanaImg} width={24} height={24} />
            <p>
              {t('header.connectChainWallet', {
                chain: 'Solana',
              })}
            </p>
          </LoginButton>
        )}
        {!chain.isSignedIn && chain.chainId === 'eth' && (
          <LoginButton
            onClick={() =>
              onClickLogin('eth', () => {
                if (!walletClient && ethAccount.isConnected) {
                  // reset wagmi state
                  resetWagmiState();
                  return;
                }
                ethModalOpen();
              })
            }
          >
            <img src={EthImg} width={24} height={24} />
            <p>
              {t('header.connectChainWallet', {
                chain: 'Eth',
              })}
            </p>
          </LoginButton>
        )}
      </div>
    </div>
  );
};

const LoginButton = twc.button`
  text-base md:text-lg text-white text-opacity-80 font-semibold
  flex justify-center items-center gap-2
  h-12 md:h-[56px] w-full rounded-full
  border border-white border-opacity-10
  bg-[#1F2636]
`;

export default Portfolio;
